import React, { Suspense, useEffect, useState } from "react";
import CommonLayout from "../../layouts/commonLayout/CommonLayout";
import { NavLink, Outlet, useParams } from "react-router-dom";
import baseUrl from "../../api/baseUrl";
import PatientModal from "./PatientModal";
import PersonIcon from "@mui/icons-material/Person";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicationIcon from "@mui/icons-material/Medication";
import HistoryIcon from "@mui/icons-material/History";
import EventNoteIcon from "@mui/icons-material/EventNote";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaymentIcon from "@mui/icons-material/Payment";

const calculateAge = (dateOfBirth?: string) => {
  if (!dateOfBirth) return null;
  try {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  } catch {
    return null;
  }
};

const toTitleCase = (str?: string) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

function AddTreatment() {
  const [patientData, setpatientData] = useState({
    userName: "",
    regNo: "",
    image: "",
    phonnum: "",
    gender: "",
    dob: "",
  });
  const [data, setdata] = useState();
  const [close, setclose] = useState(false);
  const { ID } = useParams();

  useEffect(() => {
    baseUrl
      .get("treatment/find-treatment", {
        headers: { treatmentid: ID },
      })
      .then((response) => {
        setdata(response.data.data.patientId);
        setpatientData({
          userName: response.data.data.patientId.fullName,
          regNo: response.data.data.patientId.regNo,
          phonnum: response.data.data.patientId.mobileNo,
          image: response.data.data.patientId.image,
          gender: response.data.data.patientId.gender || "",
          dob: response.data.data.patientId.dob || "",
        });
      })
      .catch((error) => console.log(error));
  }, [ID]);

  const handleCloseModal = () => setclose(false);
  const handleViewPatientClick = () => setclose(true);

  const navItems = [
    { path: `diagnose/${ID}`, label: "DIAGNOSE", icon: LocalHospitalIcon },
    { path: `treatment-plan/${ID}`, label: "TREATMENT PLAN", icon: EventNoteIcon },
    { path: `treatment-table/${ID}`, label: "TREATMENT", icon: MedicationIcon },
    { path: `drugs/${ID}`, label: "DRUGS", icon: MedicationIcon },
    { path: `past-treatment/${ID}`, label: "PAST TREATMENT", icon: HistoryIcon },
    { path: `treatment-planned/${ID}`, label: "PLANNED", icon: EventNoteIcon },
    { path: `past-invoice/${ID}`, label: "PAYMENT", icon: PaymentIcon },
  ];

  return (
    <CommonLayout>
      <div className="mb-4">
        {/* Patient Profile Card */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {patientData?.image ? (
                <img
                  src={patientData.image}
                  alt={patientData.userName}
                  className="w-16 h-16 rounded-lg object-cover border border-slate-200 dark:border-slate-600"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-primary to-primaryLight flex items-center justify-center border border-slate-200 dark:border-slate-600">
                  <PersonIcon className="text-white" sx={{ fontSize: 32 }} />
                </div>
              )}
            </div>

            {/* Patient Info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mb-2">
                {toTitleCase(patientData?.userName) || "Patient Name"}
              </h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-500 dark:text-slate-400 font-medium uppercase">Reg No:</span>
                  <span className="text-slate-800 dark:text-slate-200 font-bold">{patientData?.regNo || "N/A"}</span>
                </div>
                {patientData?.phonnum && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-500 dark:text-slate-400 font-medium uppercase">Mobile:</span>
                    <span className="text-slate-800 dark:text-slate-200 font-bold">{patientData.phonnum}</span>
                  </div>
                )}
                {patientData?.gender && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-500 dark:text-slate-400 font-medium uppercase">Gender:</span>
                    <span className="text-slate-800 dark:text-slate-200 font-bold">{toTitleCase(patientData.gender)}</span>
                  </div>
                )}
                {patientData?.dob && calculateAge(patientData.dob) !== null && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-500 dark:text-slate-400 font-medium uppercase">Age:</span>
                    <span className="text-slate-800 dark:text-slate-200 font-bold">{calculateAge(patientData.dob)} Years</span>
                  </div>
                )}
              </div>
            </div>

            {/* View Details Button */}
            <div className="flex-shrink-0">
              <button
                onClick={handleViewPatientClick}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium text-xs hover:shadow-lg hover:shadow-primary/20 transition-all whitespace-nowrap"
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
                Patient Details
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-100 dark:border-slate-700 p-1 flex flex-wrap gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  isActive
                    ? "bg-gradient-to-r from-primary to-primaryLight text-white shadow-lg shadow-primary/20"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                }`
              }
            >
              <item.icon sx={{ fontSize: 14 }} />
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Content Outlet */}
      <Suspense
        fallback={
          <div className="fixed inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        }
      >
        <Outlet />
      </Suspense>

      {close && <PatientModal patient={data} onClose={handleCloseModal} />}
    </CommonLayout>
  );
}

export default AddTreatment;
